import {
  AnimatedImageDto,
  BulkDownloadAnimatedImagesDto,
  CreateAnimatedImageDto,
  CreateAnimatedImageResponseDto,
  CreateCustomInputAnimatedImageDto,
  DeleteAnimatedImagesResponseDto,
  GetAnimatedImagesByExtIdsResponseDto,
  GetQuickAnimationCreditsResponseDto,
  GetAnimatedImagesByOriginVideoExternalIdResponseDto,
  GetAnimatedImagesQuery,
  InitializeSlowMotionDto,
  PagedAnimatedImagesResponse,
  UploadAnimatedImageResponseDto,
} from "@repo/api/animated-image";
import { AmplifilesClient } from "./api-client";
import { AnalyticsApi } from "./analytics-api";

export class AnimatedImageApi {
  static async getAnimatedImagesByOriginVideoExternalId(
    teamExternalId: string,
    videoExternalId: string,
  ): Promise<GetAnimatedImagesByOriginVideoExternalIdResponseDto> {
    const response = await AmplifilesClient.instance.get(
      `/team/${teamExternalId}/video/${videoExternalId}/animated-images`,
    );

    return GetAnimatedImagesByOriginVideoExternalIdResponseDto.parse(
      response.data,
    );
  }

  static async getPagedAnimatedImages(
    teamExternalId: string,
    query: GetAnimatedImagesQuery,
  ): Promise<PagedAnimatedImagesResponse> {
    const response = await AmplifilesClient.instance.get(
      `/team/${teamExternalId}/animated-image`,
      { params: query },
    );

    return PagedAnimatedImagesResponse.parse(response.data);
  }

  static async getAnimatedImagesByExtIds(
    teamExternalId: string,
    externalIds: string[],
  ): Promise<GetAnimatedImagesByExtIdsResponseDto> {
    if (externalIds.length === 0) {
      return {};
    }

    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/animated-image-by-ext-ids`,
      { externalIds },
    );

    return GetAnimatedImagesByExtIdsResponseDto.parse(response.data);
  }

  static async getQuickAnimationCredits(
    teamExternalId: string,
    sourceImageExternalIds: string[],
  ): Promise<GetQuickAnimationCreditsResponseDto> {
    if (sourceImageExternalIds.length === 0) {
      return { credits: 0 };
    }

    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/quick-animation-credits`,
      { sourceImageExternalIds },
    );

    return GetQuickAnimationCreditsResponseDto.parse(response.data);
  }

  static async createAnimatedImages(
    teamExternalId: string,
    data: CreateAnimatedImageDto[],
  ): Promise<CreateAnimatedImageResponseDto[]> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/animated-image`,
      data,
    );

    AnalyticsApi.logAnimateImageTriggered();

    return CreateAnimatedImageResponseDto.array().parse(response.data);
  }

  static async createCustomInputAnimatedImage(
    teamExternalId: string,
    data: CreateCustomInputAnimatedImageDto,
  ): Promise<AnimatedImageDto> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/animated-image/custom-prompt`,
      data,
    );

    return AnimatedImageDto.parse(response.data);
  }

  static async uploadAnimatedImage(
    teamExternalId: string,
    videoExternalId: string,
    video: File,
  ): Promise<UploadAnimatedImageResponseDto> {
    const formData = new FormData();
    formData.append("video", video);

    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/video/${videoExternalId}/animated-image/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return UploadAnimatedImageResponseDto.parse(response.data);
  }

  static async initializeSlowMotion(
    teamExternalId: string,
    data: InitializeSlowMotionDto,
  ): Promise<AnimatedImageDto> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/slow-motion`,
      data,
    );

    return AnimatedImageDto.parse(response.data);
  }

  static async deleteAnimatedImages(
    teamExternalId: string,
    externalIds: string[],
  ): Promise<DeleteAnimatedImagesResponseDto> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/animated-images/delete`,
      { externalIds },
    );

    return DeleteAnimatedImagesResponseDto.parse(response.data);
  }

  static async bulkDownloadAnimatedImages(
    teamExternalId: string,
    data: BulkDownloadAnimatedImagesDto,
  ): Promise<void> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/animated-images/bulk-download`,
      data,
      {
        responseType: "blob",
      },
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;

    const contentDisposition = response.headers["content-disposition"];
    let filename = "amplifiles-original-clips.zip";
    if (contentDisposition) {
      const matches = contentDisposition.match(/filename="([^"]+)"/);
      if (matches && matches[1]) {
        filename = matches[1];
      }
    }

    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}
